import { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { format, subYears } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import {
  GetColorStatus,
  ScheduledTransactionsList,
  capitalizeFirstLetterOfWords,
  formattedCurrency,
  withStyles,
  Box,
} from 'vopay';
import { CopyButton, TransactionStatus, Button } from 'UI';
import { useTranslation } from 'react-i18next';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useClientData } from '@/providers/ClientData.provider';
import useFetchTransactionsHistory from '@/hooks/useApi/useFetchTransactionsHistory';
import useFetchScheduledTransactions from '@/hooks/useApi/useFetchScheduledTransactions';
import Complete from '@/components/UI/Complete/Complete';
import styles from './MyAccountConfirmationPage.module.scss';

const cn = withStyles(styles);

type Flow = 'fund' | 'withdraw';
type Category = 'transaction' | 'scheduled';

export default function MyAccountConfirmationPage() {
  const { transactionId, currency, flow, category } = useParams<{
    transactionId: string;
    currency: string;
    flow: Flow;
    category: Category;
  }>();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const accountName = queryParams.get('accountName') || '';
  const accountDetails = queryParams.get('accountDetails') || '';
  const paymentMethod = queryParams.get('paymentMethod') === 'credit' ? 'CreditCard' : 'BankAccount';
  const fundingAmount = queryParams.get('fundingAmount') || '';

  const history = useHistory();
  const alertDialog = useAlertDialog();
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.MyAccountConfirmation' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const {
    response: { Country },
  } = useClientData();

  const [scheduledTransaction, setScheduledTransaction] = useState<ScheduledTransactionsList>();
  const { transactions, fetchTransactionsHistory } = useFetchTransactionsHistory();
  const [, , , fetchScheduledTransactions] = useFetchScheduledTransactions({});

  useEffect(() => {
    if (category !== 'transaction' || !transactionId) return;

    async function loadTransaction() {
      try {
        await fetchTransactionsHistory({
          StartDateTime: format(subYears(new Date(), 5), 'yyyy-MM-dd'),
          EndDateTime: formatInTimeZone(new Date(), 'UTC', 'yyyy-MM-dd'),
          TransactionID: transactionId,
        });
      } catch (error) {
        alertDialog.error(
          tGlobal('error', { defaultValue: 'Error' }),
          error instanceof Error ? error.message : String(error),
        );
      }
    }

    loadTransaction();
  }, [transactionId, category]);

  useEffect(() => {
    if (category !== 'scheduled' || !transactionId) return;

    async function loadScheduled() {
      try {
        const res = await fetchScheduledTransactions({ ScheduledTransactionID: transactionId });
        if (res.Success && res.ScheduledTransactions) {
          const items = Object.values(res.ScheduledTransactions);
          if (items.length > 0) setScheduledTransaction(items[0]);
        }
      } catch (error) {
        alertDialog.error(
          tGlobal('error', { defaultValue: 'Error' }),
          error instanceof Error ? error.message : String(error),
        );
      }
    }

    loadScheduled();
  }, [transactionId, category]);

  const currentTransaction = useMemo(
    () => transactions.find((tx) => tx.TransactionID === transactionId),
    [transactions, transactionId],
  );

  const amount = useMemo(() => {
    if (fundingAmount) return fundingAmount;
    if (category === 'scheduled') return scheduledTransaction?.Amount || '0.00';
    const credit = currentTransaction?.CreditAmount;
    const debit = currentTransaction?.DebitAmount;
    return credit && credit !== '0.00' ? credit : debit || '0.00';
  }, [fundingAmount, category, currentTransaction, scheduledTransaction]);

  const status = useMemo(() => {
    return currentTransaction?.TransactionStatus || 'Pending';
  }, [currentTransaction]);

  const txCurrency = useMemo(() => {
    if (category === 'scheduled') return scheduledTransaction?.Currency || currency;
    return currentTransaction?.Currency || currency;
  }, [category, currentTransaction, scheduledTransaction, currency]);

  const actionText = useMemo(() => {
    return flow === 'fund'
      ? t('funding', { defaultValue: 'Funding' })
      : t('withdrawing', { defaultValue: 'Withdrawing' });
  }, [flow, t]);

  const recipientText = useMemo(() => {
    if (!accountName) return undefined;
    return (
      <span>
        <span className={cn('fontGrey')}>
          {flow === 'fund' ? t('to', { defaultValue: 'to' }) : t('from', { defaultValue: 'from' })}{' '}
        </span>
        <strong className={cn('fontBlack')}>
          {currency} {accountName} {accountDetails}
        </strong>
      </span>
    );
  }, [flow, accountName, accountDetails, currency, t]);

  const handleViewDetails = () => {
    if (category === 'scheduled') {
      history.replace(`/scheduled-transactions/details/${transactionId}/`);
    } else {
      const txType =
        flow === 'fund'
          ? Country === 'US'
            ? 'ACHFunding'
            : 'EFTFunding'
          : Country === 'US'
            ? 'ACHWithdrawal'
            : 'EFTWithdrawal';
      history.replace(`/transactions-history/details/${txType}/${transactionId}/${txCurrency}/`);
    }
  };

  const details = useMemo(
    () => [
      {
        label:
          category === 'scheduled'
            ? t('scheduleId', { defaultValue: 'Schedule ID' })
            : t('transactionId', { defaultValue: 'Transaction ID' }),
        value: (
          <Box gapSize="8px" ai="center" jc="fe">
            <span>{transactionId}</span>
            <CopyButton content={transactionId} />
          </Box>
        ),
      },
      {
        label: t('status', { defaultValue: 'Status' }),
        value: (
          <Box jc="fe">
            <TransactionStatus
              status={GetColorStatus(status)}
              label={tGlobal(`Statuses.${status?.toLowerCase()}`, {
                defaultValue: capitalizeFirstLetterOfWords(status || ''),
              })}
            />
          </Box>
        ),
      },
      ...(accountName
        ? [
            {
              label: t('paymentMethod', { defaultValue: 'Payment Method' }),
              value: tGlobal(`PaymentMethodTypes.${paymentMethod}`, {
                defaultValue: paymentMethod === 'CreditCard' ? 'Credit Card' : 'Bank Account',
              }),
            },
            {
              label: t('paymentMethodDetails', { defaultValue: 'Payment Method Details' }),
              value: accountDetails,
            },
          ]
        : []),
    ],
    [transactionId, status, category, accountName, accountDetails, paymentMethod, t, tGlobal],
  );

  return (
    <Complete
      title={t('title', { defaultValue: 'Transaction Created' })}
      amount={`${txCurrency} ${formattedCurrency(String(amount))}`}
      actionText={actionText}
      recipientText={recipientText}
      showAmounts={true}
      details={details}
      className={cn('container')}
    >
      <div className={cn('buttonContainer')}>
        <Button size="small" onClick={handleViewDetails}>
          {t('viewDetails', { defaultValue: 'View Transaction Details' })}
        </Button>
        <Button
          size="small"
          colorScheme="primary"
          onClick={() => history.replace(flow === 'fund' ? '/fund-my-account' : '/withdraw-my-account')}
        >
          {flow === 'fund'
            ? t('newFundTransaction', { defaultValue: 'New Fund Transaction' })
            : t('newWithdrawTransaction', { defaultValue: 'New Withdraw Transaction' })}
        </Button>
      </div>
    </Complete>
  );
}
